import React from 'react';
import { AlertCircle, X, CreditCard, Shield, Archive, ExternalLink } from 'lucide-react';

/**
 * SubscriptionExpirationHandler
 * 
 * Handles different states of subscription expiration:
 * - 0 to -30 days: Closable warning with renewal options
 * - -30 to -90 days: Non-closable critical warning
 * - After -90 days: Archived message and logout
 */
const SubscriptionExpirationHandler = ({ daysLeft, isOpen, onClose, onLogout }) => {
  if (!isOpen || daysLeft > 0) return null;

  const daysOverdue = Math.abs(daysLeft);

  // After -90 days: Feed is archived, show message and force logout
  if (daysOverdue > 90) {
    return (
      <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm overflow-y-auto">
        <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full mx-4 my-8 overflow-hidden">
          {/* Header */}
          <div className="bg-gradient-to-br from-gray-600 to-gray-800 p-6">
            <div className="flex items-center justify-center mb-4">
              <div className="w-16 h-16 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center">
                <Archive className="w-10 h-10 text-white" />
              </div>
            </div>
            <h2 className="text-2xl font-bold text-white text-center">
              Feed Archivado
            </h2>
          </div>

          {/* Content */}
          <div className="p-6 text-center max-h-[60vh] overflow-y-auto">
            <p className="text-gray-700 text-lg mb-4">
              Tu feed ha sido archivado debido a la falta de renovación por más de 90 días.
            </p>
            <p className="text-gray-600 text-sm mb-6">
              Para reactivar tu servicio, por favor contacta con soporte.
            </p>
            
            <button
              onClick={onLogout}
              className="w-full bg-gradient-to-r from-gray-600 to-gray-800 hover:from-gray-700 hover:to-gray-900 text-white px-6 py-3 rounded-xl font-semibold shadow-lg transition-all duration-200"
            >
              Cerrar Sesión
            </button>
          </div>
        </div>
      </div>
    );
  }

  // -30 to -90 days: Non-closable critical warning
  const isNonClosable = daysOverdue > 30;

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-fadeIn overflow-y-auto">
      <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full mx-4 my-8 animate-slideUp overflow-hidden">
        {/* Header with gradient */}
        <div className={`bg-gradient-to-br ${isNonClosable ? 'from-red-600 to-red-800' : 'from-amber-500 to-orange-600'} p-6 relative`}>
          {!isNonClosable && (
            <button
              onClick={onClose}
              className="absolute top-4 right-4 text-white/80 hover:text-white transition-colors"
              aria-label="Cerrar"
            >
              <X className="w-6 h-6" />
            </button>
          )}
          
          <div className="flex items-center justify-center mb-4">
            <div className="w-16 h-16 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center">
              <AlertCircle className="w-10 h-10 text-white" />
            </div>
          </div>
          
          <h2 className="text-2xl font-bold text-white text-center">
            {isNonClosable ? '¡Acción Requerida Urgente!' : 'Licencia Vencida'}
          </h2>
          <p className="text-white/90 text-center mt-2">
            {daysOverdue} {daysOverdue === 1 ? 'día' : 'días'} vencido
          </p>
        </div>

        {/* Content */}
        <div className="p-6 max-h-[60vh] overflow-y-auto">
          {isNonClosable && (
            <div className="bg-red-50 border-2 border-red-200 rounded-lg p-4 mb-6">
              <p className="text-red-800 font-semibold text-center mb-2">
                ⚠️ Tu cuenta será archivada en {90 - daysOverdue} días
              </p>
              <p className="text-red-700 text-sm text-center">
                Es urgente que renueves tu licencia para evitar la pérdida de acceso.
              </p>
            </div>
          )}

          <div className="mb-6">
            <p className="text-gray-700 text-center mb-4 font-medium">
              Tienes dos opciones disponibles:
            </p>
            
            {/* Option 1: Renew LisualPro */}
            <div className="bg-gradient-to-br from-blue-50 to-indigo-50 border-2 border-blue-200 rounded-xl p-5 mb-4 hover:shadow-lg transition-all">
              <div className="flex items-start">
                <div className="flex-shrink-0 w-10 h-10 bg-blue-600 rounded-full flex items-center justify-center mr-3">
                  <CreditCard className="w-5 h-5 text-white" />
                </div>
                <div className="flex-1">
                  <h3 className="font-bold text-gray-900 mb-2">Renovar LisualPro</h3>
                  <p className="text-sm text-gray-700 mb-3">
                    Continúa generando contenido de marketing del avance de tu obra
                  </p>
                  <a
                    href="https://buy.stripe.com/6oU14o8Gtb8s5ua8By33W02"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg font-semibold text-sm transition-all shadow-md hover:shadow-lg"
                  >
                    Renovar Ahora
                    <ExternalLink className="w-4 h-4" />
                  </a>
                </div>
              </div>
            </div>

            {/* Option 2: Archive and Security Only */}
            <div className="bg-gradient-to-br from-gray-50 to-slate-50 border-2 border-gray-200 rounded-xl p-5 hover:shadow-lg transition-all">
              <div className="flex items-start">
                <div className="flex-shrink-0 w-10 h-10 bg-gray-600 rounded-full flex items-center justify-center mr-3">
                  <Shield className="w-5 h-5 text-white" />
                </div>
                <div className="flex-1">
                  <h3 className="font-bold text-gray-900 mb-2">Archivar Proyecto</h3>
                  <p className="text-sm text-gray-700 mb-3">
                    No renovar y seguir accediendo a la cámara exclusivamente como solución de seguridad
                  </p>
                  <a
                    href="https://buy.stripe.com/00w00k2i5a4o3m26tq33W0f"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 bg-gray-600 hover:bg-gray-700 text-white px-4 py-2 rounded-lg font-semibold text-sm transition-all shadow-md hover:shadow-lg"
                  >
                    Archivar y Continuar
                    <ExternalLink className="w-4 h-4" />
                  </a>
                </div>
              </div>
            </div>
          </div>

          {/* Additional info */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <p className="text-sm text-blue-800 text-center">
              <strong>¿Ya renovaste?</strong> Si realizaste el pago recientemente, 
              puedes ignorar este mensaje. El sistema se actualizará automáticamente.
            </p>
          </div>

          {/* Close button (only for 0 to -30 days) */}
          {!isNonClosable && (
            <button
              onClick={onClose}
              className="w-full mt-4 text-gray-500 hover:text-gray-700 px-6 py-2 text-sm font-medium transition-colors"
            >
              Cerrar temporalmente
            </button>
          )}
        </div>
      </div>

      <style jsx>{`
        @keyframes fadeIn {
          from {
            opacity: 0;
          }
          to {
            opacity: 1;
          }
        }

        @keyframes slideUp {
          from {
            transform: translateY(20px);
            opacity: 0;
          }
          to {
            transform: translateY(0);
            opacity: 1;
          }
        }

        .animate-fadeIn {
          animation: fadeIn 0.2s ease-out;
        }

        .animate-slideUp {
          animation: slideUp 0.3s ease-out;
        }
      `}</style>
    </div>
  );
};

export default SubscriptionExpirationHandler;

